import { Dialog } from "primereact/dialog";
import { Controller, useForm } from "react-hook-form";
import { useAuthContext } from "../../../../context/AuthContext";
import { useToast } from "../../../../context/toast/ui";
import { ResursTreeApi } from "../../../../services/modules/tree";
import styles from "./quick-create-device.module.css";
import { Dropdown } from "primereact/dropdown";
import { useEffect, useState } from "react";
import { useDeviceTree } from "../../../../services/hooks/useDeviceTree";
import { Button } from "primereact/button";
import {
  InputNumber,
  InputNumberValueChangeEvent,
} from "primereact/inputnumber";
import { Checkbox } from "primereact/checkbox";
import { useSelector } from "react-redux";
import { RootState } from "../../../../services/types/store-types";

const initialTreeData: ResursTreeApi.TreeDataItems = {
  channel: {
    name: "",
    className: "",
    count: 1,
    active: true,
  },
  iface: {
    name: "",
    className: "",
    count: 1,
    active: true,
  },
  device: {
    name: "",
    className: "",
    count: 1,
    active: true,
    isRegistrator: false,
  },
};

function itemsToArray(items: ResursTreeApi.TCreateDeviceTreeSelectResponse) {
  let optionArr: string[] = [];
  items.list.map((item) => optionArr.push(item.name));
  return optionArr;
}

function getClassName(
  items: ResursTreeApi.TCreateDeviceTreeSelectResponse,
  name: string,
) {
  return items.list.find((e) => e.name === name)?.className || "";
}

export const QuickCreateModalDevice = (props: {
  show: boolean;
  setShow: (value: boolean) => void;
  tree: ResursTreeApi.treeName;
}) => {
  const { show, setShow } = props;
  const { control, handleSubmit } = useForm();
  const { userRole } = useAuthContext();
  const { showMessage } = useToast();
  const [levelVisible, setLevelVisible] = useState<number>(1);
  const [treeData, setTreeData] =
    useState<ResursTreeApi.TreeDataItems>(initialTreeData);

  const lastSelect = useSelector(
    (state: RootState) => state.tree.selectedNodes,
  );

  const selectedObjectId = [lastSelect.objectIds[0]];

  const {
    channels,
    interfaces,
    devices,
    registrators,
    fetchChannels,
    fetchInterfaces,
    fetchDevices,
    fetchRegistrators,
    clearRegistrators,
  } = useDeviceTree();

  useEffect(() => {
    fetchChannels({
      level: "Channel",
      objectId: selectedObjectId,
    });
  }, []);

  const handleChangeChannel = (
    channelName: string,
    count: number,
    active: boolean = true,
  ) => {
    if (registrators) clearRegistrators();
    const className = getClassName(channels!, channelName);
    console.log(lastSelect.objectIds[0]);
    setTreeData((prev) => ({
      ...prev,
      channel: {
        name: channelName,
        className,
        count,
        active,
      },
      interface: initialTreeData.iface,
      device: initialTreeData.device,
      regDevice: undefined,
    }));

    fetchInterfaces({
      level: "Iface",
      selectedClass: className,
      objectId: selectedObjectId,
    });
    setLevelVisible(2);
  };

  const handleChangeInterface = (
    interfaceName: string,
    count: number,
    active: boolean = true,
  ) => {
    if (registrators) clearRegistrators();
    const className = getClassName(interfaces!, interfaceName);
    setTreeData((prev) => ({
      ...prev,
      iface: {
        name: interfaceName,
        className,
        count,
        active,
      },
      device: initialTreeData.device,
      regDevice: undefined,
    }));

    fetchDevices({
      level: "Device",
      selectedClass: className,
      objectId: selectedObjectId,
    });
    setLevelVisible(3);
  };

  const handleChangeDevice = (
    deviceName: string,
    count: number,
    active: boolean = true,
  ) => {
    if (registrators) clearRegistrators();
    const isRegistrator = devices?.list.find(
      (data) => data.name === deviceName,
    )?.isRegistrator;
    const className = getClassName(devices!, deviceName);
    setTreeData((prev) => ({
      ...prev,
      device: {
        name: deviceName,
        className,
        count,
        active,
        isRegistrator: isRegistrator === true ? true : false,
      },
      regDevice: undefined,
    }));
    if (isRegistrator) {
      fetchRegistrators({
        level: "RegDevice",
        selectedClass: className,
        objectId: selectedObjectId,
      });
    }
    setLevelVisible(4);
  };

  const handleChangeRegistrator = (
    registratorName: string,
    count: number,
    active: boolean = true,
  ) => {
    const className = getClassName(registrators!, registratorName);
    setTreeData((prev) => ({
      ...prev,
      regDevice: {
        name: registratorName,
        className,
        count,
        active,
      },
    }));
    setLevelVisible(5);
  };

  const handleCountChange = (
    level: keyof ResursTreeApi.TreeDataItems,
    count: number,
  ) => {
    setTreeData((prev) => ({
      ...prev,
      [level]: {
        ...prev[level],
        count,
      },
    }));
  };

  const handleActiveChange = (
    level: keyof ResursTreeApi.TreeDataItems,
    active: boolean,
  ) => {
    setTreeData((prev) => ({
      ...prev,
      [level]: {
        ...prev[level],
        active,
      },
    }));
  };

  const onSubmit = () => {
    if (userRole === "usr") {
      showMessage({
        caption: "Ошибка",
        message: "У вас нет прав!",
        tag: "add",
        timeOut: 3,
        type: "error",
      });
      return;
    }

    const submitData: ResursTreeApi.TCreateDeviceTree = {
      objectId: [lastSelect.objectIds[0]],
      options: {
        channel: {
          className: treeData.channel.className,
          count: treeData.channel.count,
          active:
            treeData.channel.className === "-"
              ? false
              : treeData.channel.active,
        },
        iface: {
          className: treeData.iface.className,
          count: treeData.iface.count,
          active: treeData.iface.active,
        },
        device: {
          className: treeData.device.className,
          count: treeData.device.count,
          active: treeData.device.active,
        },
        ...(treeData.regDevice && {
          regDevice: {
            className: treeData.regDevice.className,
            count: treeData.regDevice.count,
            active: treeData.regDevice.active,
          },
        }),
      },
    };

    ResursTreeApi.createDeviceTree(submitData);

    setShow(false);
  };

  return (
    <Dialog
      className={styles.dialogHeader}
      dismissableMask
      header="Быстрое создание дерева устройств"
      style={{ width: 550 }}
      visible={show}
      onHide={() => {
        setShow(false);
      }}
    >
      <form
        data-at-wc-quick-create-modal-form
        onSubmit={handleSubmit(onSubmit)}
        className={styles.form}
      >
        <div className={styles.description}>
          Давайте быстро и просто создадим дерево устройств. Укажите необходимые
          параметры и нажмите «Создать».
        </div>

        {channels && (
          <Controller
            name="channel"
            control={control}
            render={({ field }) => (
              <div className={styles.formGroup}>
                <div className={styles.labelsContaiber}>
                  <label className={styles.dropdownLabel}>
                    Выберите тип нового канала связи:
                  </label>
                  <label className={styles.inputNumberLabel}>Кол-во</label>
                </div>
                <div className={styles.inputGroupDevice}>
                  <Dropdown
                    size={3}
                    value={treeData.channel.name}
                    options={itemsToArray(channels)}
                    disabled={levelVisible >= 1 ? false : true}
                    placeholder="Выберите канал"
                    panelClassName={styles.dropdownPanel}
                    onChange={(e) =>
                      handleChangeChannel(
                        e.value,
                        treeData.channel.count,
                        treeData.channel.active,
                      )
                    }
                    checkmark={true}
                    highlightOnSelect={true}
                    className={styles.dropdown}
                  />
                  <InputNumber
                    disabled={
                      levelVisible >= 1
                        ? treeData.channel.className === "-"
                          ? true
                          : false
                        : true
                    }
                    size={2}
                    value={treeData.channel.count}
                    onValueChange={(e: InputNumberValueChangeEvent) =>
                      handleCountChange("channel", e.value || 1)
                    }
                    mode="decimal"
                    showButtons
                    min={1}
                    className={styles.inputNumber}
                  />
                </div>
                <div className={styles.checkboxContainer}>
                  <Checkbox
                    inputId="channel-active"
                    checked={treeData.channel.active}
                    onChange={(e) =>
                      handleActiveChange("channel", e.checked || false)
                    }
                    disabled={
                      levelVisible >= 1
                        ? treeData.channel.className === "-"
                          ? true
                          : false
                        : true
                    }
                  />
                  <label className={styles.checkboxLabel}>Активировать</label>
                </div>
              </div>
            )}
          />
        )}

        <Controller
          name="iface"
          control={control}
          render={({ field }) => (
            <div className={styles.formGroup}>
              <div className={styles.labelsContaiber}>
                <label className={styles.dropdownLabel}>
                  Выберите интерфейс:
                </label>
                <label className={styles.inputNumberLabel}>Кол-во</label>
              </div>
              <div className={styles.inputGroupDevice}>
                <Dropdown
                  value={treeData.iface.name}
                  disabled={
                    levelVisible >= 2 &&
                    interfaces &&
                    interfaces.list.length !== 0
                      ? false
                      : true
                  }
                  options={interfaces ? itemsToArray(interfaces) : []}
                  placeholder="Выберите интерфейс"
                  panelClassName={styles.dropdownPanel}
                  onChange={(e) =>
                    handleChangeInterface(
                      e.value,
                      treeData.iface.count,
                      treeData.iface.active,
                    )
                  }
                  checkmark={true}
                  highlightOnSelect={true}
                  className={styles.dropdown}
                />
                <InputNumber
                  disabled={
                    levelVisible >= 2 &&
                    interfaces &&
                    interfaces.list.length !== 0
                      ? false
                      : true
                  }
                  size={2}
                  value={treeData.iface.count}
                  onValueChange={(e: InputNumberValueChangeEvent) =>
                    handleCountChange("iface", e.value || 1)
                  }
                  mode="decimal"
                  showButtons
                  min={1}
                  className={styles.inputNumber}
                />
              </div>
              <div className={styles.checkboxContainer}>
                <Checkbox
                  inputId="interface-active"
                  checked={treeData.iface.active}
                  onChange={(e) =>
                    handleActiveChange("iface", e.checked || false)
                  }
                  disabled={
                    levelVisible >= 2 &&
                    interfaces &&
                    interfaces.list.length !== 0
                      ? false
                      : true
                  }
                />
                <label className={styles.checkboxLabel}>Активировать</label>
              </div>
            </div>
          )}
        />

        <Controller
          name="device"
          control={control}
          render={({ field }) => (
            <div className={styles.formGroup}>
              <div className={styles.labelsContaiber}>
                <label className={styles.dropdownLabel}>
                  Выберите устройство:
                </label>
                <label className={styles.inputNumberLabel}>Кол-во</label>
              </div>
              <div className={styles.inputGroupDevice}>
                <Dropdown
                  value={treeData.device.name}
                  disabled={levelVisible >= 3 ? false : true}
                  options={devices ? itemsToArray(devices) : []}
                  placeholder="Выберите устройство"
                  panelClassName={styles.dropdownPanel}
                  onChange={(e) =>
                    handleChangeDevice(
                      e.value,
                      treeData.device.count,
                      treeData.device.active,
                    )
                  }
                  checkmark={true}
                  highlightOnSelect={true}
                  className={styles.dropdown}
                />
                <InputNumber
                  disabled={levelVisible >= 3 ? false : true}
                  value={treeData.device.count}
                  onValueChange={(e: InputNumberValueChangeEvent) =>
                    handleCountChange("device", e.value || 1)
                  }
                  size={2}
                  mode="decimal"
                  showButtons
                  min={1}
                  className={styles.inputNumber}
                />
              </div>
              <div className={styles.checkboxContainer}>
                <Checkbox
                  inputId="device-active"
                  checked={treeData.device.active}
                  onChange={(e) =>
                    handleActiveChange("device", e.checked || false)
                  }
                  disabled={levelVisible >= 3 ? false : true}
                />
                <label className={styles.checkboxLabel}>Активировать</label>
              </div>
            </div>
          )}
        />

        {registrators && (
          <Controller
            name="registrator"
            control={control}
            render={({ field }) => (
              <div className={styles.formGroup}>
                <div className={styles.labelsContaiber}>
                  <label className={styles.dropdownLabel}>
                    Выберите счетчик:
                  </label>
                  <label className={styles.inputNumberLabel}>Число</label>
                </div>
                <div className={styles.inputGroupDevice}>
                  <Dropdown
                    value={treeData.regDevice?.name || ""}
                    disabled={levelVisible >= 4 ? false : true}
                    options={itemsToArray(registrators)}
                    placeholder="Выберите счетчик"
                    panelClassName={styles.dropdownPanel}
                    onChange={(e) =>
                      handleChangeRegistrator(
                        e.value,
                        treeData.regDevice?.count || 1,
                        treeData.regDevice?.active || true,
                      )
                    }
                    checkmark={true}
                    highlightOnSelect={true}
                    className={styles.dropdown}
                  />
                  <InputNumber
                    disabled={levelVisible >= 4 ? false : true}
                    size={2}
                    value={treeData.regDevice?.count || 1}
                    onValueChange={(e: InputNumberValueChangeEvent) => {
                      if (treeData.regDevice) {
                        handleChangeRegistrator(
                          treeData.regDevice.name,
                          e.value || 1,
                          treeData.regDevice.active,
                        );
                      }
                    }}
                    mode="decimal"
                    showButtons
                    min={1}
                    className={styles.inputNumber}
                  />
                </div>
                <div className={styles.checkboxContainer}>
                  <Checkbox
                    inputId="registrator-active"
                    checked={treeData.regDevice?.active!}
                    onChange={(e) =>
                      handleActiveChange("regDevice", e.checked || false)
                    }
                    disabled={levelVisible >= 4 ? false : true}
                  />
                  <label className={styles.checkboxLabel}>Активировать</label>
                </div>
              </div>
            )}
          />
        )}

        <Button
          disabled={
            (!registrators && levelVisible === 4) ||
            (registrators && levelVisible === 5)
              ? false
              : true
          }
          label="Создать"
          size="small"
          type="submit"
          data-at-wc-quick-create-modal-submit
          className={styles.submitButton}
        />
      </form>
    </Dialog>
  );
};
